import React from "react";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  HiOutlineDocumentText,
  HiShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineMail,
  HiBadgeCheck,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineClock,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import { FaUserSecret } from "react-icons/fa";

import { useGetVerificationData } from "../../../hooks/esignature";
import LanguageSelector from "../../atoms/LanguageSelector";
import BusinessCardFooter from "../../atoms/BusinessCardFooter";
import DocumentIntegrityCheck from "./DocumentIntegrityCheck";

const SUM_ANIMATED_GIF_URL =
  "https://cdn.1cdn.app/application/UMD/22103111455394_loadingsum.gif";

const VerificationPage: React.FC = () => {
  const { t, i18n } = useTranslation("clientView");
  const { entityId = "", userId = "", requestId = "" } = useParams();

  const { data, isLoading, isError } = useGetVerificationData(
    entityId,
    userId,
    requestId
  );

  const formatDate = (iso?: string) => {
    if (!iso) return "—";
    try {
      const formatted = new Date(iso).toLocaleString(i18n.language, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "shortOffset",
      });
      return formatted.replace(/\bGMT\b/g, "UTC");
    } catch {
      return iso;
    }
  };

  const formatSignatureType = (type?: string) => {
    switch (type) {
      case "type":
        return t("verification.signatureType.type");
      case "draw":
        return t("verification.signatureType.draw");
      case "upload":
        return t("verification.signatureType.upload");
      default:
        return type || "—";
    }
  };

  const verifiedBadge = (
    <HiBadgeCheck
      className="inline-block ms-1.5 text-green-500 align-text-bottom"
      size={18}
      aria-label={String(t("verification.signer.verified"))}
      title={String(t("verification.signer.verified"))}
    />
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50 p-4">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <img
            src={SUM_ANIMATED_GIF_URL}
            alt="Super User Manager"
            width={96}
            height={96}
          />
          <span className="text-sm">{t("verification.loading")}</span>
        </div>
      </div>
    );
  }

  const notFound = isError || !data || data.status !== "signed";

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-50 via-white to-amber-50 p-4">
        <div className="relative max-w-md w-full bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-8 text-center">
          <div className="absolute top-3 end-3 rounded-full bg-white ring-1 ring-gray-200 shadow-sm p-0.5">
            <LanguageSelector variant="compact" />
          </div>
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <HiOutlineShieldExclamation className="text-red-500" size={36} />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t("verification.notFound.title")}
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            {t("verification.notFound.message")}
          </p>
        </div>
      </div>
    );
  }

  const audit = data.audit_trail || {};
  const entity = data.entity || {};
  const contactEmail = entity.entity_contact_email;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 pb-12">
      <div className="relative bg-gradient-to-r from-green-500 to-green-600 text-white">
        <div className="absolute top-4 end-6 sm:end-8 rounded-full bg-white p-0.5 shadow-sm ring-1 ring-white/60">
          <LanguageSelector variant="compact" />
        </div>
        <div className="max-w-3xl mx-auto px-4 py-10 sm:py-12 pe-14 sm:pe-16">
          <div className="flex items-center gap-4 sm:gap-5">
            <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center ring-4 ring-white/10">
              <HiShieldCheck size={32} className="text-white sm:hidden" />
              <HiShieldCheck size={36} className="text-white hidden sm:block" />
            </div>
            <h1 className="flex-1 min-w-0 text-2xl font-bold leading-tight break-words">
              {data.signer_name
                ? t("verification.titleWithName", { name: data.signer_name })
                : t("verification.title")}
            </h1>
          </div>
          <p className="text-sm sm:text-base text-green-50 mt-3">
            {t("verification.subtitle")}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-6">
        <section className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <HiOutlineDocumentText className="text-blue-500" size={18} />
            </div>
            <h2 className="text-base font-semibold text-gray-800">
              {t("verification.document.title")}
            </h2>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div className="min-w-0">
              <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                {t("verification.document.name")}
              </dt>
              <dd className="text-gray-800 font-medium break-words">
                {data.document_title || "—"}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                {t("verification.document.requestId")}
              </dt>
              <dd className="text-gray-600 font-mono text-xs break-all">
                {data.request_id || "—"}
              </dd>
            </div>
          </dl>
        </section>

        <section className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
              <HiOutlineUser className="text-green-500" size={18} />
            </div>
            <h2 className="text-base font-semibold text-gray-800">
              {t("verification.signer.title")}
            </h2>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div className="min-w-0">
              <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                {t("verification.signer.name")}
              </dt>
              <dd className="text-gray-800 font-medium break-words">
                {data.signer_name || "—"}
              </dd>
            </div>
            {data.signer_email ? (
              <div className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1 flex items-center gap-1.5">
                  <HiOutlineMail size={12} />
                  {t("verification.signer.email")}
                </dt>
                <dd className="text-gray-700 font-mono text-sm break-all">
                  {data.signer_email}
                  {audit.is_email_verified ? verifiedBadge : null}
                </dd>
              </div>
            ) : null}
            {data.signer_phone ? (
              <div className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1 flex items-center gap-1.5">
                  <HiOutlinePhone size={12} />
                  {t("verification.signer.phone")}
                </dt>
                <dd className="text-gray-700 font-mono text-sm break-all">
                  {data.signer_phone}
                </dd>
              </div>
            ) : null}
            {audit.is_phone_verified ? (
              <div className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1 flex items-center gap-1.5">
                  <HiOutlinePhone size={12} />
                  {t("verification.signer.phoneVerified")}
                </dt>
                {audit.verified_phone_masked ? (
                  <dd className="text-gray-700 font-mono text-sm break-all">
                    {audit.verified_phone_masked}
                    {verifiedBadge}
                  </dd>
                ) : (
                  <dd className="text-gray-700 text-sm break-words">
                    {t("verification.signer.phoneVerifiedUnknownNumber")}
                    {verifiedBadge}
                  </dd>
                )}
              </div>
            ) : null}
          </dl>
        </section>

        <section className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 mb-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                <HiOutlineClock className="text-amber-500" size={20} />
              </div>
              <div className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                  {t("verification.timeline.signed")}
                </dt>
                <dd className="text-gray-800 font-medium text-sm mt-0.5 break-words">
                  {formatDate(audit.signed_at)}
                </dd>
              </div>
            </div>
            {audit.signature_type ? (
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <HiOutlineDocumentText className="text-gray-500" size={20} />
                </div>
                <div className="min-w-0">
                  <dt className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                    {t("verification.timeline.signatureType")}
                  </dt>
                  <dd className="text-gray-800 font-medium text-sm mt-0.5 break-words">
                    {formatSignatureType(audit.signature_type)}
                  </dd>
                </div>
              </div>
            ) : null}
          </dl>
        </section>

        <section className="bg-blue-50 rounded-xl shadow-sm ring-1 ring-blue-100 p-5 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
              <HiOutlineOfficeBuilding className="text-blue-500" size={20} />
            </div>
            <div className="text-sm text-gray-700 leading-relaxed">
              <p>
                {t("verification.contact.message")}{" "}
                <span className="font-semibold text-gray-800">
                  {entity.entity_name ||
                    t("verification.contact.entityFallback")}
                </span>
                {contactEmail ? ` ${t("verification.contact.at")}` : "."}
              </p>
              {contactEmail ? (
                <a
                  href={`mailto:${contactEmail}`}
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium hover:underline break-all mt-1"
                >
                  <HiOutlineMail size={14} />
                  {contactEmail}
                </a>
              ) : null}
            </div>
          </div>
        </section>

        {audit.document_hash_sha256 && (
          <DocumentIntegrityCheck storedHash={audit.document_hash_sha256} />
        )}

        <section className="flex items-start gap-2.5 px-5 py-3 mb-6 text-xs text-gray-500 leading-relaxed">
          <FaUserSecret className="shrink-0 text-gray-400 mt-0.5" size={14} />
          <p>
            <Trans
              t={t}
              i18nKey="verification.authenticity.note"
              components={{ strong: <strong /> }}
            />
          </p>
        </section>

        <BusinessCardFooter showFooter={true} theme="modern" />
      </div>
    </div>
  );
};

export default VerificationPage;
